<template>
    <p-modal
        ref="customerAccountDetailsModal"
        :button-label="buttonLabel"
        size="lg"
        :label="modalTitle"
        @ok.prevent="handleModalOk"
        @show="loadData"
        @hide="checkDirtyBeforeModalClose($event)"
    >
        <template v-slot:default>
            <p-form v-if="customerDetails && customerList" ref="form">
                <b-row cols="1">
                    <b-col>
                        <p-advanced-select
                            label="Customer Name"
                            :data-type="isNew ? 'customers' : undefined"
                            :options="isNew ? undefined : selectedCustomerOption"
                            v-model="customerDetails.customerId"
                            :disabled="!isNew"
                            :filter="filterCustomer"
                            rules="required"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" lg="6">
                        <p-datepicker
                            label="Start Date"
                            rules="required"
                            vid="startDate"
                            v-model="customerDetails.startDate"
                        />
                    </b-col>
                    <b-col cols="12" lg="6">
                        <!-- todo: make end date -->
                        <p-datepicker
                            label="End Date"
                            vid="endDate"
                            rules="required|dateGreaterOrEqual:@startDate"
                            v-model="customerDetails.endDate"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" lg="4">
                        <p-number
                            :disabled="account.type === 2 || account.noLimit"
                            input-type="money"
                            label="Credit Limit"
                            rules="min_value:0"
                            v-model="customerDetails.limit"
                            @change="creditLimitChangeHandler()"
                        />
                        <span v-if="account.noLimit" style="color:red">No Limit</span>
                    </b-col>
                    <b-col cols="12" lg="4">
                        <p-number
                            input-type="money"
                            label="Customer Balance"
                            v-model="customerDetails.balance"
                            @change="customerBalanceChangeHandler()"
                        />
                    </b-col>
                    <b-col cols="12" lg="4">
                        <p-number
                            disabled="true"
                            input-type="money"
                            label="Available Funds"
                            v-model="customerDetails.availableFunds"
                        />
                    </b-col>
                </b-row>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row class="w-100">
                <b-col class="mb-3 text-end pe-0">
                    <p-button
                        variant="outline-primary"
                        @click="cancel"
                        class="ms-2"
                        :is-busy="isBusy"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        class="ms-2"
                        :is-busy="isBusy"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>
<script>
import axios from 'axios';
import NavigationGuard from '@/components/mixins/NavigationGuard.js';

export default {
    mixins: [NavigationGuard],
    props: {
        buttonLabel: String,
        customerId: Number,
        details: Object,
        account: Object,
        id: {
            default: 'new',
            type: String
        }
    },
    data() {
        return {
            customerDetails: null,
            isBusy: false,
            customerList: Array
        };
    },
    computed: {
        isNew() {
            return this.id === 'new';
        },
        modalTitle() {
            return this.account.accountCode + ' ' + this.account.agencyName;
        },
        // In Edit mode the Customer Name field is disabled and only needs to show
        // the already-linked customer. Seeding the select with that one option
        // (built from the row `details`, mirroring the /SelectLists/customers text
        // format) avoids fetching the entire customer list — which on large data
        // sets was slow or left the name blank. New mode still loads the full list
        // via data-type so the user can pick.
        selectedCustomerOption() {
            if (this.isNew) return [];
            const d = this.details;
            if (d) {
                const email = d.email ? ` ${d.email}` : '';
                return [{
                    value: this.customerId,
                    text: `${d.lastName}, ${d.firstName} (${d.customerAccount})${email}`
                }];
            }
            // Fallback if the row wasn't passed in: use the loaded detail's name.
            const cd = this.customerDetails;
            if (cd) {
                return [{ value: this.customerId, text: `${cd.lastName}, ${cd.firstName}` }];
            }
            return [];
        }
    },
    watch: {
        customerDetails: NavigationGuard.$watcher
    },
    methods: {
        loadData() {
            this.isBusy = true;
            let dataPromise;
            if (this.isNew) {
                this.customerList = null;
                dataPromise = axios.get('AccountReceivable/newcustomer', {
                    params: {
                        accountId: this.account.accountId
                    }
                });
                axios
                    .get('accountreceivable/customersearch', {
                        params: {
                            perPage: 100,
                            automatic: '',
                            searchBy: 'All',
                            accountId: this.account.accountId
                        }
                    })
                    .then(resp => {
                        this.customerList = resp.data.data;
                    });
            } else {
                dataPromise = axios.get('AccountReceivable/customer', {
                    params: {
                        accountId: this.account.accountId,
                        customerId: this.customerId
                    }
                });
            }

            dataPromise
                .then(response => {
                    const details = response.data;
                    details.accountId = this.account.accountId;
                    if (this.isNew) {
                        details.startDate = this.account.defaultStartDate;
                        details.endDate = this.account.defaultEndDate;
                        details.limit = this.account.defaultCreditLimit;
                        details.balance = this.account.defaultBalance;
                    }
                    else{                        
                        details.limit = this.account.noLimit ? null :  this.account.type === 2 ? this.account.defaultCreditLimit : details.limit;
                    }
                    this.customerDetails = details;
                    // Reset the dirty baseline after the load settles (dataLoaded
                    // defers past the deep-watch flush the assignment above triggers).
                    this.dataLoaded();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        handleModalOk: async function() {
            if (!(await this.$refs.form.validate())) {
                return;
            }
            this.isBusy = true;
            const details = { ...this.customerDetails };
            if(this.account.noLimit){
                details.limit = null;
            }
            else if (this.account.type === 2) {
                details.limit = details.limit === null ? 0 : details.limit;
            }
            details.balance = details.balance === null ? 0 : details.balance;
            details.isDebit = this.account.isDebit;
            axios
                .post('AccountReceivable/customer', details)
                .then(() => {
                    this.dataSaved();
                    this.$emit('result-updated');
                    this.$refs.customerAccountDetailsModal.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        filterCustomer(customer) {
            return (
                !this.isNew ||
                !this.customerList?.some(x => x.customerId === customer.value)
            );
        },
        creditLimitChangeHandler() {
            if (this.account.type === 2) {
                this.customerDetails.availableFunds = this.customerDetails.balance;
            }else {
                this.customerDetails.availableFunds =
                    this.customerDetails.limit - this.customerDetails.balance;
            }
        },
        customerBalanceChangeHandler() {

            if (this.account.type === 2) {
                this.customerDetails.availableFunds = this.customerDetails.balance;
            }else {
                this.customerDetails.availableFunds =
                    this.customerDetails.limit - this.customerDetails.balance;
            }
        },
        checkDirtyBeforeModalClose(bvModalEvt) {
            const trigger = bvModalEvt?.trigger;
            // The OK button fires the modal's built-in `ok`; handleModalOk does
            // the real save and closes the modal itself, so swallow the ok-close
            // here and never prompt for it.
            if (trigger === 'ok') {
                bvModalEvt.preventDefault();
                return;
            }
            // Only a genuine user dismissal (clicking out/backdrop, Esc, Cancel,
            // the header X) should warn about unsaved changes. Our own post-save
            // `.hide()` comes through with trigger null and closes cleanly.
            const userDismissed = ['backdrop', 'cancel', 'close', 'esc'].includes(trigger);
            if (!userDismissed || !this.formIsDirty) return;
            bvModalEvt.preventDefault();
            if (this._dirtyCheckInProgress) return;
            this._dirtyCheckInProgress = true;
            this.$bvModal.msgBoxConfirm([this.displayMessage]).then(value => {
                this._dirtyCheckInProgress = false;
                if (value) {
                    this.dataSaved();
                    this.$nextTick(() => {
                        this.$refs.customerAccountDetailsModal?.hide();
                    });
                }
            });
        }
    }
};
</script>
